import { type ComponentChildren, createContext } from 'preact'
import { useCallback, useContext } from 'preact/hooks'
import type { ConversationSummary } from '../api/generated/ConversationSummary'
import type { GroupSummary } from '../api/generated/GroupSummary'
import type { Shift } from '../api/generated/Shift'
import type { Volunteer } from '../api/generated/Volunteer'
import { useApi } from '../api/useApi'
import { useEvent } from '../events/EventContext'
import { useLiveReload } from '../live/useLiveReload'

type OrganizerData = {
  shifts: ReturnType<typeof useApi<Shift[]>>
  conversations: ReturnType<typeof useApi<ConversationSummary[]>>
  groups: ReturnType<typeof useApi<GroupSummary[]>>
  volunteers: ReturnType<typeof useApi<Volunteer[]>>
}

const OrganizerDataContext = createContext<OrganizerData | null>(null)

/** Data of the navigation badges, kept live across the sections and shared with their pages. */
export function OrganizerDataProvider({
  slug,
  children,
}: {
  slug: string
  children: ComponentChildren
}) {
  const shifts = useApi<Shift[]>(`/events/${slug}/shifts`)
  const conversations = useApi<ConversationSummary[]>(`/events/${slug}/conversations`)
  const groups = useApi<GroupSummary[]>(`/events/${slug}/groups`)
  const volunteers = useApi<Volunteer[]>(`/events/${slug}/volunteers`)
  const event = useEvent()
  // Every write under the event signals, reading a conversation included
  const reload = useCallback(() => {
    shifts.reload()
    conversations.reload()
    groups.reload()
    volunteers.reload()
    event.reload()
  }, [shifts.reload, conversations.reload, groups.reload, volunteers.reload, event.reload])
  useLiveReload(slug, reload)
  return (
    <OrganizerDataContext.Provider value={{ shifts, conversations, groups, volunteers }}>
      {children}
    </OrganizerDataContext.Provider>
  )
}

export function useOrganizerData(): OrganizerData {
  const data = useContext(OrganizerDataContext)
  if (!data) throw new Error('useOrganizerData outside OrganizerDataProvider')
  return data
}
